import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Cpu, Coins, Lightning, ChartLineUp, WarningCircle, ArrowRight, Robot,
} from "@phosphor-icons/react";

const fmtInt = (n) => (n || 0).toLocaleString("pt-BR");
const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4,
  });
const fmtBRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: 2,
  });
const fmtMs = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}s`;
  return `${Math.round(v)}ms`;
};
const fmtTokens = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} k`;
  return v.toString();
};
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

const ENGINE_LABEL = {
  agent_orchestrator: "Atendimento IA (Sonnet)",
  vehicle_vision:     "Leitura CRLV (Vision)",
  help_assistant:     "Assistente do AgentUS",
  refine:             "Reescrever resposta",
  rewrite_template:   "Reescrever template",
};

function HeroKpi({ label, value, hint, accent, testId, icon: Icon }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-6 min-w-0 hover:bg-white/[0.05] transition-colors" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">{label}</div>
        {Icon && <Icon size={14} weight="regular" className="text-slate-500" />}
      </div>
      <div className={`font-heading text-[30px] leading-none font-semibold tracking-tight mt-3 tabular-nums ${accent || "text-white"}`}>{value}</div>
      {hint && <div className="text-[11.5px] text-slate-500 mt-2.5">{hint}</div>}
    </div>
  );
}

function SegBtn({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3.5 py-1 rounded-full text-[12px] font-medium transition-colors ${
        active
          ? "bg-white text-slate-900"
          : "text-slate-400 hover:text-white hover:bg-white/5"
      }`}
    >{label}</button>
  );
}

function Timeline({ daily }) {
  if (!daily?.length) {
    return <div className="text-[12px] text-slate-500 italic">Sem dados na janela selecionada.</div>;
  }
  const maxCalls = Math.max(...daily.map((d) => d.calls), 1);
  return (
    <div className="flex items-stretch gap-2 h-36" data-testid="ai-obs-timeline">
      {daily.map((d) => {
        const h = d.calls > 0
          ? Math.max(4, Math.round((d.calls / maxCalls) * 100))
          : 0;
        return (
          <div
            key={d.date}
            className="flex-1 h-full flex flex-col items-center justify-end gap-1.5 min-w-0 group"
            data-testid={`ai-obs-timeline-day-${d.date}`}
          >
            <div
              className="text-[10px] text-slate-300 tabular-nums opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-3"
              title={`${d.calls} chamadas · ${fmtUSD(d.cost_usd)} · ${fmtTokens(d.tokens || 0)} tokens`}
            >
              {fmtInt(d.calls)}
            </div>
            <div
              className="w-full flex-1 bg-white/5 rounded-sm relative overflow-hidden"
              title={`${d.date} · ${d.calls} chamadas · ${fmtUSD(d.cost_usd)} · ${fmtTokens(d.tokens || 0)} tokens`}
            >
              <div
                className="absolute bottom-0 left-0 right-0 bg-emerald-400/70 group-hover:bg-emerald-400 rounded-sm transition-all"
                style={{ height: `${h}%` }}
              />
            </div>
            <div className="text-[10px] text-slate-500 tabular-nums shrink-0">
              {d.date.slice(5)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminAIObservability() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/ai/observability?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const s = data?.summary || {};

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ai-observability-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-1.5">
            <Robot size={11} weight="regular" /> Observabilidade IA
          </div>
          <h1 className="font-heading text-[24px] font-semibold tracking-tight text-white">
            Observabilidade da IA do AgentUS
          </h1>
          <div className="text-[12.5px] text-slate-400 mt-1.5 max-w-2xl">
            Visão cross-tenant de consumo, custo e latência das chamadas LLM. Atualizado em tempo real.
          </div>
        </div>
        <div className="inline-flex items-center gap-1 bg-white/5 rounded-full p-1 border border-white/10">
          {[1, 7, 30, 90].map((d) => (
            <SegBtn key={d} active={days === d} onClick={() => setDays(d)} label={`${d}d`} testId={`ai-obs-range-${d}`} />
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-[13px] text-slate-400" data-testid="ai-obs-loading">Carregando…</div>
      )}
      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4 text-[13px] text-rose-300 mb-4" data-testid="ai-obs-error">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Hero KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <HeroKpi
              label="Chamadas LLM"
              value={fmtInt(s.total_calls)}
              hint={`${s.total_failures || 0} falhas · taxa de sucesso ${fmtPct(s.success_rate)}`}
              testId="ai-obs-kpi-calls"
              icon={Cpu}
            />
            <HeroKpi
              label="Custo estimado"
              value={fmtUSD(s.total_cost_usd)}
              hint={`≈ ${fmtBRL(s.cost_brl_at_5)} (USD@5)`}
              testId="ai-obs-kpi-cost"
              icon={Coins}
              accent="text-emerald-300"
            />
            <HeroKpi
              label="Tokens (in + out)"
              value={`${fmtTokens(s.total_tokens_in)} / ${fmtTokens(s.total_tokens_out)}`}
              hint="Estimativa ~3,2 chars/token (PT-BR)"
              testId="ai-obs-kpi-tokens"
              icon={ChartLineUp}
            />
            <HeroKpi
              label="Latência média"
              value={fmtMs(s.avg_latency_ms)}
              hint="Round-trip end-to-end por chamada"
              testId="ai-obs-kpi-latency"
              icon={Lightning}
            />
          </div>

          {/* Timeline */}
          <div className="bg-white/[0.02] border border-white/10 rounded-lg p-5 mb-6" data-testid="ai-obs-daily-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Volume diário</div>
                <div className="text-[13px] text-slate-300 mt-1">Chamadas LLM por dia · barras proporcionais ao máximo</div>
              </div>
            </div>
            <Timeline daily={data.daily} />
          </div>

          {/* Per Engine */}
          <div className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden mb-6" data-testid="ai-obs-per-engine">
            <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Consumo por engine</div>
                <div className="text-[13px] text-slate-300 mt-0.5">Onde a IA está sendo gasta</div>
              </div>
              <div className="text-[11px] text-slate-500">{data.per_engine.length} engines</div>
            </div>
            <table className="w-full min-w-[720px] text-[12.5px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500 bg-white/[0.02]">
                  <th className="px-5 py-2.5 font-medium">Engine</th>
                  <th className="px-3 py-2.5 font-medium text-right">Chamadas</th>
                  <th className="px-3 py-2.5 font-medium text-right">Sucesso</th>
                  <th className="px-3 py-2.5 font-medium text-right">Tokens in</th>
                  <th className="px-3 py-2.5 font-medium text-right">Tokens out</th>
                  <th className="px-3 py-2.5 font-medium text-right">Latência</th>
                  <th className="px-3 py-2.5 font-medium text-right">Custo USD</th>
                  <th className="px-3 py-2.5 font-medium text-right">Tenants</th>
                </tr>
              </thead>
              <tbody>
                {data.per_engine.map((e) => (
                  <tr key={e.engine} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`ai-obs-engine-row-${e.engine}`}>
                    <td className="px-5 py-3 text-white">
                      <div className="font-medium">{ENGINE_LABEL[e.engine] || e.engine}</div>
                      <div className="text-[10.5px] text-slate-500 mt-0.5 font-mono">{e.engine}</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-300">{fmtInt(e.calls)}</td>
                    <td className={`px-3 py-3 text-right tabular-nums ${e.success_rate < 0.95 ? "text-amber-300" : "text-emerald-300"}`}>
                      {fmtPct(e.success_rate)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-400">{fmtTokens(e.tokens_in)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-400">{fmtTokens(e.tokens_out)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-300">{fmtMs(e.avg_latency_ms)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-300 font-medium">{fmtUSD(e.cost_usd)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-400">{e.tenants_using}</td>
                  </tr>
                ))}
                {!data.per_engine.length && (
                  <tr><td colSpan={8} className="px-5 py-6 text-center text-slate-500 text-[12.5px] italic">Sem chamadas LLM no período.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Per Tenant */}
          <div className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden mb-6" data-testid="ai-obs-per-tenant">
            <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Top tenants por custo</div>
                <div className="text-[13px] text-slate-300 mt-0.5">Quem está consumindo IA — ordenado pelo custo USD</div>
              </div>
              <div className="text-[11px] text-slate-500">{data.per_tenant.length} tenants</div>
            </div>
            <table className="w-full min-w-[720px] text-[12.5px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500 bg-white/[0.02]">
                  <th className="px-5 py-2.5 font-medium">Tenant</th>
                  <th className="px-3 py-2.5 font-medium text-right">Chamadas</th>
                  <th className="px-3 py-2.5 font-medium text-right">Tokens</th>
                  <th className="px-3 py-2.5 font-medium text-right">Engines</th>
                  <th className="px-3 py-2.5 font-medium text-right">Latência</th>
                  <th className="px-3 py-2.5 font-medium text-right">Custo USD</th>
                </tr>
              </thead>
              <tbody>
                {data.per_tenant.slice(0, 25).map((t) => (
                  <tr key={t.tenant_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`ai-obs-tenant-row-${t.tenant_id}`}>
                    <td className="px-5 py-3 text-white">
                      <div className="font-medium truncate max-w-xs">{t.tenant_name}</div>
                      <div className="text-[10.5px] text-slate-500 font-mono mt-0.5">{t.tenant_id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-300">{fmtInt(t.calls)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-400">{fmtTokens(t.tokens_in + t.tokens_out)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-400">{t.engines_used}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-300">{fmtMs(t.avg_latency_ms)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-300 font-medium">{fmtUSD(t.cost_usd)}</td>
                  </tr>
                ))}
                {!data.per_tenant.length && (
                  <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-500 text-[12.5px] italic">Sem consumo por tenant no período.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Per Model + Errors */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden" data-testid="ai-obs-per-model">
              <div className="px-5 py-3.5 border-b border-white/10">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Por modelo</div>
                <div className="text-[13px] text-slate-300 mt-0.5">Distribuição entre os modelos LLM em uso</div>
              </div>
              <table className="w-full min-w-[720px] text-[12.5px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500 bg-white/[0.02]">
                    <th className="px-5 py-2.5 font-medium">Modelo</th>
                    <th className="px-3 py-2.5 font-medium text-right">Chamadas</th>
                    <th className="px-3 py-2.5 font-medium text-right">Tokens</th>
                    <th className="px-3 py-2.5 font-medium text-right">Custo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_model.map((m) => (
                    <tr key={m.model} className="border-t border-white/5">
                      <td className="px-5 py-3 text-white font-mono text-[11.5px]">{m.model}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-300">{fmtInt(m.calls)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-400">{fmtTokens(m.tokens_in + m.tokens_out)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-emerald-300 font-medium">{fmtUSD(m.cost_usd)}</td>
                    </tr>
                  ))}
                  {!data.per_model.length && (
                    <tr><td colSpan={4} className="px-5 py-6 text-center text-slate-500 text-[12.5px] italic">Sem dados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-white/[0.02] border border-white/10 rounded-lg overflow-hidden" data-testid="ai-obs-errors">
              <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Erros do provider</div>
                  <div className="text-[13px] text-slate-300 mt-0.5">Tipos de falha agrupados (debug)</div>
                </div>
                {!!data.errors.length && <WarningCircle size={14} weight="regular" className="text-amber-400" />}
              </div>
              <table className="w-full min-w-[720px] text-[12.5px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-slate-500 bg-white/[0.02]">
                    <th className="px-5 py-2.5 font-medium">Tipo de erro</th>
                    <th className="px-3 py-2.5 font-medium text-right">Ocorrências</th>
                  </tr>
                </thead>
                <tbody>
                  {data.errors.map((e) => (
                    <tr key={e.error_kind} className="border-t border-white/5">
                      <td className="px-5 py-3 text-white font-mono text-[11.5px]">{e.error_kind}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-amber-300">{fmtInt(e.count)}</td>
                    </tr>
                  ))}
                  {!data.errors.length && (
                    <tr><td colSpan={2} className="px-5 py-6 text-center text-emerald-300/80 text-[12.5px] italic">Nenhuma falha no período.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 rounded-lg border border-white/5 bg-white/[0.02] p-4 text-[11.5px] text-slate-500 flex items-center gap-2" data-testid="ai-obs-footer">
            <ArrowRight size={12} weight="regular" className="text-slate-500" />
            <span>
              Janela: {data.window_days} dias · desde {String(data.since).slice(0, 10)} · Estimativas baseadas em
              ~3,2 caracteres por token (PT-BR) e pricing oficial Anthropic Feb/2026.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
